import { mat4, vec3 } from 'gl-matrix';
import { Camera } from '../camera';

/**
 * Pushes the active camera's world-space position + orientation into the
 * AudioContext's listener. Forward and up are extracted from the view matrix
 * (rows 2 and 1 respectively, with forward negated to convert from view space
 * `-Z` to world-space forward).
 */
export class AudioListener {
  private readonly tmpPos = vec3.create();
  private readonly tmpFwd = vec3.create();
  private readonly tmpUp = vec3.create();

  constructor(private readonly ctx: AudioContext) {}

  update(camera: Camera): void {
    const view = camera.modelViewMatrix as mat4;
    extractListenerBasis(view, this.tmpPos, this.tmpFwd, this.tmpUp);
    applyListener(this.ctx.listener, this.tmpPos, this.tmpFwd, this.tmpUp, this.ctx.currentTime);
  }
}

/**
 * Extract camera world-space position, forward, and up from its view matrix.
 *
 *   view * worldVec = viewVec  (worldVec = view⁻¹ * viewVec)
 *
 * For an orthonormal view matrix, rows of `view` are view-space basis axes
 * expressed in world space. With column-major (gl-matrix) storage the row j of
 * the matrix is `[m[j], m[j+4], m[j+8]]`. We use:
 *   - row 1 → camera up in world space
 *   - row 2 → camera back (i.e. `+Z`) in world space; forward = -row2
 *
 * Position is recovered by `-Rᵀ * t` where R is the rotation block and
 * t = `[m[12], m[13], m[14]]`.
 *
 * Exported so tests can validate without a real AudioContext.
 */
export function extractListenerBasis(
  view: mat4,
  outPos: vec3,
  outForward: vec3,
  outUp: vec3,
): void {
  // Rotation rows. (See header for derivation.)
  outUp[0] = view[1];
  outUp[1] = view[5];
  outUp[2] = view[9];

  outForward[0] = -view[2];
  outForward[1] = -view[6];
  outForward[2] = -view[10];

  // Position: -Rᵀ * t. Each output component i sums down a *column* of R
  // (three consecutive raw entries V[i*4 + j]), NOT across a row — the row
  // form computes R * t, which silently swaps the listener to a mirrored
  // world position whenever the camera has non-identity rotation.
  const tx = view[12];
  const ty = view[13];
  const tz = view[14];
  outPos[0] = -(view[0] * tx + view[1] * ty + view[2] * tz);
  outPos[1] = -(view[4] * tx + view[5] * ty + view[6] * tz);
  outPos[2] = -(view[8] * tx + view[9] * ty + view[10] * tz);
}

function applyListener(
  listener: AudioListener_AudioApi,
  pos: vec3,
  fwd: vec3,
  up: vec3,
  when: number,
): void {
  if (listener.positionX) {
    listener.positionX.setValueAtTime(pos[0], when);
    listener.positionY!.setValueAtTime(pos[1], when);
    listener.positionZ!.setValueAtTime(pos[2], when);
    listener.forwardX!.setValueAtTime(fwd[0], when);
    listener.forwardY!.setValueAtTime(fwd[1], when);
    listener.forwardZ!.setValueAtTime(fwd[2], when);
    listener.upX!.setValueAtTime(up[0], when);
    listener.upY!.setValueAtTime(up[1], when);
    listener.upZ!.setValueAtTime(up[2], when);
  } else if (listener.setPosition && listener.setOrientation) {
    listener.setPosition(pos[0], pos[1], pos[2]);
    listener.setOrientation(fwd[0], fwd[1], fwd[2], up[0], up[1], up[2]);
  }
}

// Shape used only for older-Safari fallback typing.
type AudioListener_AudioApi = {
  positionX?: AudioParam;
  positionY?: AudioParam;
  positionZ?: AudioParam;
  forwardX?: AudioParam;
  forwardY?: AudioParam;
  forwardZ?: AudioParam;
  upX?: AudioParam;
  upY?: AudioParam;
  upZ?: AudioParam;
  setPosition?: (x: number, y: number, z: number) => void;
  setOrientation?: (fx: number, fy: number, fz: number, ux: number, uy: number, uz: number) => void;
};
